import { useState, useEffect, useRef } from 'react'
import { ArrowSquareRightIcon } from '@phosphor-icons/react'
import { ChatMessage, LeftDrawer, DemoModeIndicator, ChatInputBox } from './components'
import { useWebSocket } from './hooks/useWebSocket'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useChats } from './hooks/useChats'
import { useMessages } from './hooks/useMessages'
import { createAIChatHandler, formatChatRequest } from './handlers/chatMessageHandlers'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { usePromptLimit } from './hooks/usePromptLimit'
import { Messages } from './components/Messages/Messages'
import { ScoopButton, useToast } from '../../../components/ui'
import { EVENTS, trackEvent } from '../../../events'
import styles from './SocketChat.module.scss'
import { useOnboarding, ONBOARDING_STEPS } from '../../../api/hooks/onboarding/useOnboarding'
import { ONBOARDING_GRACE_PERIOD_DAYS } from '../../../api/hooks/onboarding/onboardingApi'
import { useUserInfo } from '../../../api/hooks/user/useUserInfo'
import { useModal, MODALS } from '../../modals'

export const SocketChat = ({
    selectedInbox,
    setSelectedInbox,
    isPublic,
    publicConfig,
    isEmbedded,
    embedConfig = {},
    setSelectedTab,
    mlConfig,
    setMlConfig,
    setMLResults,
    onAuthError,
    isTourWorkspace,
}) => {
    const userID = useSelector((state) => state.auth?.userID)
    const workspaceID = useSelector((state) => state.auth?.workspaceID)
    const storageKeyPrefix = `scoop_chat_${userID}_${workspaceID}`
    const [inputMessage, setInputMessage] = useState('')
    const [aiStatus, setAiStatus] = useState(null)
    const [isCreatingChat, setIsCreatingChat] = useState(false)
    const [processingSteps, setProcessingSteps] = useState([]) // Track all processing steps
    const [analysisMode, setAnalysisMode] = useState(() => {
        if (isPublic || !userID || !workspaceID) return 'quick'
        return localStorage.getItem(`${storageKeyPrefix}_analysisMode`) || 'quick'
    })
    const [showProcessingSteps, setShowProcessingSteps] = useState(() => {
        if (isPublic || !userID || !workspaceID) return true
        return localStorage.getItem(`${storageKeyPrefix}_showProcessingSteps`) !== 'false'
    })
    const [showQueryDetails, setShowQueryDetails] = useState(() => {
        if (isPublic || !userID || !workspaceID) return true
        return localStorage.getItem(`${storageKeyPrefix}_showQueryDetails`) !== 'false'
    })
    const [drawerOpen, setDrawerOpen] = useState(!isPublic) // Hide drawer in demo/public/embedded mode
    const lastUserQueryRef = useRef('') // Ref to avoid re-renders
    const processingStepsRef = useRef([]) // Ref to access steps without re-renders
    const lastFollowUpPromptsRef = useRef([]) // Track last follow-up prompts for error recovery
    // Track if we've sent the initial dataset description
    const [hasDescribedDataset, setHasDescribedDataset] = useState(false)
    // Track recently created chats to avoid fetching empty history
    const [recentlyCreatedChatID, setRecentlyCreatedChatID] = useState(null)
    // Track if we've done initial chat restoration to prevent re-running
    const [hasRestoredInitialChat, setHasRestoredInitialChat] = useState(false)
    // Track the last chat ID that had messages loaded to prevent race conditions
    const [lastLoadedChatID, setLastLoadedChatID] = useState(null)
    // Track when we're actively switching between chats
    const [isSwitchingChats, setIsSwitchingChats] = useState(false)
    // Track which chats have had messages saved to backend (need invalidation when switching away)
    const [chatsWithSavedMessages, setChatsWithSavedMessages] = useState(new Set())
    // Track the last message count to detect new messages vs initial load
    const [lastMessageCount, setLastMessageCount] = useState(0)
    // Track scroll position before pagination to restore it after
    const [scrollPositionBeforePagination, setScrollPositionBeforePagination] = useState(null)

    // Use the workspace metadata hook like in Chat.jsx
    const { workspaceMetadata } = useWorkspaceMetadata()

    // User info for onboarding account age check
    const { user, isLoading: isLoadingUser } = useUserInfo()

    // Onboarding tracking
    const { completeStep, isStepCompleted, isOnboardingComplete, loadingOnboarding } =
        useOnboarding()

    // Toast notifications for onboarding hints
    const toast = useToast()

    // Account age check — existing users don't need onboarding toasts or modals
    const accountAgeDays = user?.createDate
        ? (Date.now() - user.createDate) / (1000 * 60 * 60 * 24)
        : 0
    const isExistingUser = accountAgeDays > ONBOARDING_GRACE_PERIOD_DAYS

    // Location state for navigation params (e.g., showOnboardingPrompts from questionnaire)
    const location = useLocation()
    const showOnboardingPromptsFromNav = location?.state?.showOnboardingPrompts
    const createdInboxFromNav = location?.state?.createdInbox

    // Onboarding modals (Modal Manager)
    const onboardingQuestionModal = useModal(MODALS.ONBOARDING.QUESTION)
    const onboardingCompleteModal = useModal(MODALS.ONBOARDING.COMPLETE)
    const onboardingModalShownRef = useRef(false)
    const [pendingOnboardingPrompt, setPendingOnboardingPrompt] = useState(null)

    // Get inbox name for the onboarding modal (modal handles fetching prompts internally)
    const inboxNameForOnboarding = createdInboxFromNav?.inboxName || selectedInbox?.inboxName

    // Prompt limit for demo/public mode
    const { promptsRemaining, canSendPrompt, incrementPromptCount, getLimitMessage } =
        usePromptLimit(isPublic)

    // Chat management with persistence for authenticated users
    const {
        persistentChats,
        loadingChats,
        currentChat,
        createNewChat,
        selectChat,
        reset,
        clearAllChats,
        setCurrentChatFromBackend,
        createChat,
        updateChat,
        deleteChat,
        isChatDeleting,
    } = useChats(isPublic)

    const {
        loadingChatMessages,
        chatMessagesError,
        invalidateMessages,
        convertedMessages,
        hasMoreMessages,
        addMessage,
        loadMoreMessages,
        isFetchingMessages,
        clearAllMessages,
        submitFeedback,
        isSubmittingFeedback,
    } = useMessages(currentChat?.chatID || null, recentlyCreatedChatID, isPublic)

    // WebSocket connection
    const { isConnected, lastMessage, sendMessage, error, clearLastMessage } = useWebSocket(
        isPublic,
        publicConfig
    )

    // Modern AI chat centered mode for full experience - don't center while loading chats
    const [isCenteredMode, setIsCenteredMode] = useState(!isPublic && !currentChat && !loadingChats)

    // Update centered mode when chats finish loading or when current chat changes
    useEffect(() => {
        if (!isPublic && !loadingChats) {
            // Set centered mode based on current chat state and messages
            if (currentChat || convertedMessages.length > 0) {
                setIsCenteredMode(false)
            } else {
                setIsCenteredMode(true)
            }
        }
    }, [loadingChats, currentChat, convertedMessages.length, isPublic])

    // Clear messages immediately when switching chats to prevent race conditions
    useEffect(() => {
        if (
            currentChat?.chatID &&
            currentChat.chatID !== lastLoadedChatID &&
            !currentChat.isTemporary && // Don't clear messages for newly created chats
            currentChat.chatID !== recentlyCreatedChatID // Don't clear messages for recently created chats
        ) {
            // Clear messages for the new chat immediately (only when switching between existing chats)
            // Messages now managed by useMessages - no manual clearing needed
            setLastLoadedChatID(null) // Will be set when messages are loaded
            setLastMessageCount(0) // Reset message count for new chat
            // Also clear any pending WebSocket messages to prevent them from being processed with wrong chat ID
            clearLastMessage()
        }
    }, [currentChat?.chatID, lastLoadedChatID, recentlyCreatedChatID, clearLastMessage])

    // Handle incoming WebSocket responses
    useEffect(() => {
        if (lastMessage) {
            const handler = createAIChatHandler({
                onMessage: (message) => {
                    // Mark last step as complete
                    const finalSteps = [...processingStepsRef.current]
                    if (finalSteps.length > 0) {
                        const lastStep = finalSteps[finalSteps.length - 1]
                        if (lastStep && !lastStep.endTime) {
                            lastStep.endTime = Date.now()
                        }
                    }

                    // Store follow-up prompts if they exist (check multiple locations)
                    if (message.content?.text?.followOnPrompts?.length > 0) {
                        lastFollowUpPromptsRef.current = message.content.text.followOnPrompts
                    } else if (message.content?.text?.vizTable?.followOnPrompts?.length > 0) {
                        lastFollowUpPromptsRef.current =
                            message.content.text.vizTable.followOnPrompts
                    } else if (message.content?.suggestions?.length > 0) {
                        lastFollowUpPromptsRef.current = message.content.suggestions
                    }

                    // Add the message with processing steps attached
                    // For error messages, include the original query that caused the error
                    const messageWithSteps = {
                        ...message,
                        processingSteps: finalSteps,
                        ...(message.role === 'error' && {
                            originalQuery: lastUserQueryRef.current,
                        }),
                    }

                    // Create chat if it doesn't exist (for dataset descriptions)
                    let chatToUse = currentChat
                    if (!chatToUse) chatToUse = createNewChat()

                    // Add message to chat using single source of truth
                    addMessage(messageWithSteps)

                    // Track that this chat has saved messages (for smart invalidation on chat switch)
                    trackMessageSaved(chatToUse.chatID, messageWithSteps)

                    // Complete onboarding step 1 when a successful chat response is received
                    if (
                        message.role === 'scoop' &&
                        !isExistingUser &&
                        !isStepCompleted(ONBOARDING_STEPS.ASK_QUESTION)
                    ) {
                        const hasVisualization =
                            message.content?.visualization ||
                            message.content?.text?.visualization ||
                            (message.content?.text?.view && message.content?.text?.selectedItems)

                        if (!hasVisualization) {
                            toast.info(
                                'Try asking for a chart to explore your data visually, like "Show me a chart of my trends"'
                            )
                        } else {
                            completeStep({ stepKey: ONBOARDING_STEPS.ASK_QUESTION })
                            toast.info(
                                'Try opening the chart in Explorer by clicking "Open in Explorer"'
                            )
                        }
                    }

                    setAiStatus(null)
                    setProcessingSteps([]) // Clear for next message
                    processingStepsRef.current = [] // Clear ref
                },
                onError: (error) => {
                    console.error('Chat error:', error)
                    setAiStatus(null)
                    setProcessingSteps([])
                },
                onAuthError: onAuthError,
                onStatus: {
                    onProcessingStatus: (status) => {
                        // Add new step or update existing
                        setProcessingSteps((prev) => {
                            const steps = [...prev]
                            const currentStep = steps[steps.length - 1]

                            // If this is a new message or different from last
                            if (!currentStep || currentStep.message !== status.message) {
                                // Mark previous step as complete
                                if (currentStep && !currentStep.endTime) {
                                    currentStep.endTime = Date.now()
                                }

                                // Add new step
                                steps.push({
                                    id: Date.now(),
                                    message: status.message,
                                    startTime: Date.now(),
                                    endTime: null,
                                })
                            }

                            // Update the ref as well
                            processingStepsRef.current = steps
                            return steps
                        })

                        setAiStatus({
                            type: 'processing',
                            message: status.message,
                            status: status.status,
                        })
                    },
                },
                onNeedsReasoning: (data) => {
                    // Create chat if it doesn't exist
                    let chatToUse = currentChat
                    if (!chatToUse) {
                        chatToUse = createNewChat()
                    }
                    // Use the stored last user query since backend doesn't return it
                    addMessage({
                        messageID: Date.now(),
                        text: `${data.message || 'This question might benefit from deeper analysis'}\n\nWould you like me to perform a deeper analysis? (This may take 10-60 seconds)`,
                        role: 'scoop',
                        timestamp: new Date().toISOString(),
                        needsReasoning: true,
                        originalQuery: lastUserQueryRef.current, // Use the ref to avoid re-renders
                    })
                    setAiStatus(null)
                },
                onExport: (result) => {
                    // Optional callback for export completion
                    // The handler already triggers download and shows messages
                },
            })
            handler(lastMessage)
            // Clear the message after processing to prevent re-processing on chat switches
            clearLastMessage()
        }
    }, [
        lastMessage,
        currentChat?.chatID,
        createNewChat,
        setAiStatus,
        setProcessingSteps,
        clearLastMessage,
    ]) // Dependencies: message, chat ID, and all functions used

    // Clear switching flag when loading completes
    useEffect(() => {
        if (isSwitchingChats && !loadingChatMessages) setIsSwitchingChats(false)
    }, [isSwitchingChats, loadingChatMessages])

    // Restore last selected chat when chats are loaded (only once on initial load)
    useEffect(() => {
        if (
            !isPublic &&
            persistentChats.length > 0 &&
            !currentChat &&
            userID &&
            workspaceID &&
            !hasRestoredInitialChat // Only run once
        ) {
            let lastChatID = null
            if (!isPublic && userID && workspaceID)
                lastChatID = localStorage.getItem(getLastChatKey())
            if (lastChatID) {
                const lastChat = persistentChats.find((chat) => chat.chatID === lastChatID)
                if (lastChat) {
                    selectChat(lastChatID) // Use direct selectChat here to avoid double-saving
                }
            }
            // Mark as restored regardless of whether we found a chat to restore
            setHasRestoredInitialChat(true)
        }
    }, [
        persistentChats,
        currentChat,
        userID,
        workspaceID,
        isPublic,
        selectChat,
        hasRestoredInitialChat,
        setHasRestoredInitialChat,
    ])

    // Load server messages when chat is first selected (not during active conversation)
    useEffect(() => {
        if (
            currentChat?.chatID &&
            convertedMessages.length > 0 &&
            !loadingChatMessages &&
            currentChat.chatID !== lastLoadedChatID // Ensure messages match the current chat
        ) {
            // Update current chat messages using memoized converted messages
            // Messages now managed by useMessages automatically
            // Mark this chat as having loaded messages
            setLastLoadedChatID(currentChat.chatID)
        }
    }, [convertedMessages, loadingChatMessages, currentChat?.chatID, lastLoadedChatID])

    // Auto-trigger dataset description ONCE for demo/public mode when all conditions are met
    useEffect(() => {
        if (isPublic && selectedInbox?.inboxID && userID && isConnected && !hasDescribedDataset) {
            // Mark as described immediately to prevent multiple triggers
            setHasDescribedDataset(true)

            // Reset chat and create new one for demo mode
            clearAllChats()
            const newChat = createNewChat()

            // Small delay to ensure chat is ready
            setTimeout(() => {
                // Send describe dataset request without processing status
                // Auth data will be added by useWebSocket
                const request = {
                    action: 'describeDataset',
                    inboxID: selectedInbox.inboxID,
                }
                sendMessage(request)
            }, 100)
        }
    }, [
        selectedInbox?.inboxID, // Include this so it waits for selectedInbox to be available
        userID,
        isConnected,
        hasDescribedDataset,
        isPublic,
        clearAllChats,
        createNewChat,
        sendMessage,
    ])

    // Show onboarding prompts modal for new users who haven't completed step 1
    useEffect(() => {
        // Skip if already shown this session or loading
        if (onboardingModalShownRef.current || loadingOnboarding || isLoadingUser) return

        // Skip for public/embedded modes
        if (isPublic || isEmbedded) return

        // Skip if onboarding is complete or step 1 is already done
        if (isOnboardingComplete() || isStepCompleted(ONBOARDING_STEPS.ASK_QUESTION)) return

        // Skip if account is older than grace period (existing user)
        if (isExistingUser) return

        // Show modal when:
        // 1. User came from questionnaire with showOnboardingPrompts flag, OR
        // 2. User has no chats yet (first time in chat)
        const shouldShowModal =
            showOnboardingPromptsFromNav || (!loadingChats && persistentChats.length === 0)

        if (shouldShowModal) {
            onboardingModalShownRef.current = true

            // Show modal and handle result
            onboardingQuestionModal
                .show({
                    inboxName: inboxNameForOnboarding,
                    datasetId: createdInboxFromNav?.inboxID || selectedInbox?.inboxID,
                    isTourMode: isTourWorkspace,
                })
                .then((result) => {
                    if (result?.prompt) {
                        setInputMessage(result.prompt)
                        // Store prompt - the effect will send it when connection is ready
                        setPendingOnboardingPrompt(result.prompt)
                    }
                })
        }
    }, [
        isPublic,
        isEmbedded,
        isTourWorkspace,
        loadingOnboarding,
        isLoadingUser,
        isExistingUser,
        loadingChats,
        persistentChats.length,
        showOnboardingPromptsFromNav,
        isOnboardingComplete,
        isStepCompleted,
        inboxNameForOnboarding,
        createdInboxFromNav,
        selectedInbox,
        onboardingQuestionModal,
    ])

    // Send pending onboarding prompt when connection is ready
    useEffect(() => {
        if (isConnected && pendingOnboardingPrompt) {
            const prompt = pendingOnboardingPrompt
            setPendingOnboardingPrompt(null)
            // Small delay to ensure UI is ready
            setTimeout(() => {
                handleSendMessage(prompt)
            }, 300)
        }
    }, [isConnected, pendingOnboardingPrompt])

    // Remember last selected chat in localStorage
    const getLastChatKey = () => `lastSelectedChat_${userID}_${workspaceID}`

    const saveLastSelectedChat = (chatID) => {
        if (!isPublic && userID && workspaceID) {
            localStorage.setItem(getLastChatKey(), chatID || '')
        }
    }

    // Helper function to clear all chat-related states
    const clearChatState = () => {
        // Clear any pending WebSocket messages
        clearLastMessage()
        // Clear all messages from useMessages hook
        clearAllMessages()
        // Clear current chat selection
        selectChat(null)
        // Clear input
        setInputMessage('')
        // Clear localStorage
        saveLastSelectedChat(null)
        // Reset message-related states
        setLastMessageCount(0)
        setLastLoadedChatID(null)
        // Clear pagination states
        setScrollPositionBeforePagination(null)
        // Clear recently created chat tracking
        setRecentlyCreatedChatID(null)
        // Clear chats with saved messages tracking
        setChatsWithSavedMessages(new Set())
        // Clear AI status and processing steps
        setAiStatus(null)
        setProcessingSteps([])
        // Clear follow-up prompts ref
        lastFollowUpPromptsRef.current = []
        // Clear last user query ref
        lastUserQueryRef.current = ''
        // Clear processing steps ref
        processingStepsRef.current = []
        // Return to centered mode for authenticated users
        if (!isPublic) setIsCenteredMode(true)
    }

    const sendMessageToSocket = (chatID, messageText) => {
        // Format request according to WebSocket API spec
        const request = formatChatRequest(messageText, selectedInbox.inboxID, chatID, {
            forceReasoning: analysisMode === 'deep',
        })
        // Send to backend
        sendMessage(request)
        // Initialize processing steps
        const initialSteps = [
            {
                id: Date.now(),
                message:
                    analysisMode === 'deep'
                        ? 'Starting deep analysis...'
                        : 'Processing your message...',
                startTime: Date.now(),
                endTime: null,
            },
        ]
        setProcessingSteps(initialSteps)
        processingStepsRef.current = initialSteps
        // Initial status when message is sent
        setAiStatus({
            type: 'processing',
            message:
                analysisMode === 'deep'
                    ? 'Starting deep analysis...'
                    : 'Processing your message...',
            status: 'thinking',
        })
    }

    const handleSendMessage = (messageText = null) => {
        // Clear any saved scroll position when sending new message
        setScrollPositionBeforePagination(null)

        const textToSend = messageText || inputMessage
        const userMessage = {
            messageID: Date.now(),
            text: textToSend,
            role: 'user',
            timestamp: new Date().toISOString(),
        }
        if (textToSend.trim() && isConnected) {
            trackEvent(EVENTS.SCOOP_AI.SEND_MESSAGE, { message: textToSend, analysisMode })
            if (isPublic) {
                // Check prompt limit for demo mode
                if (!canSendPrompt()) {
                    const limitMessage = getLimitMessage()
                    if (limitMessage) {
                        // Add system message about limit
                        let chatToUse = currentChat
                        if (!chatToUse) {
                            chatToUse = createNewChat()
                        }
                        addMessage({
                            messageID: Date.now(),
                            text: limitMessage,
                            role: 'system',
                            timestamp: new Date().toISOString(),
                        })
                    }
                    return
                }
                // Demo/public mode - no persistence, direct WebSocket communication
                if (!currentChat) {
                    // Create local-only chat for demo mode
                    const newChat = createNewChat()
                    addMessage(userMessage)
                    trackMessageSaved(newChat.chatID, userMessage)
                    // Send directly to WebSocket with temporary chat ID (demo mode)
                    sendMessageToSocket(newChat.chatID, textToSend)
                } else {
                    // Existing demo chat - send directly
                    addMessage(userMessage)
                    trackMessageSaved(currentChat.chatID, userMessage)
                    sendMessageToSocket(currentChat.chatID, textToSend)
                }
                // Increment prompt count for demo mode
                incrementPromptCount()
            } else {
                // Native experience
                if (!currentChat) {
                    // Chat creation routine
                    setIsCreatingChat(true) // Start loading
                    createChat({
                        title: textToSend.substring(0, 50), // Use first 50 chars as title
                        onSuccess: (response) => {
                            // Clean sequential flow:
                            if (response?.chatID) {
                                // 1. Mark this chat as recently created to prevent history fetching
                                setRecentlyCreatedChatID(response.chatID)

                                // 2. Set current chat from backend response
                                setCurrentChatFromBackend(response)

                                // 3. Add message to chat with backend chat as base (combines both operations)
                                addMessage(userMessage, response.chatID)
                                trackMessageSaved(response.chatID, userMessage)

                                // 4. Send message to socket
                                sendMessageToSocket(response.chatID, textToSend)

                                // 5. Update localStorage with the real chat ID
                                saveLastSelectedChat(response.chatID)

                                // 6. Exit centered mode when user sends first message
                                if (isCenteredMode) setIsCenteredMode(false)
                            }
                            setIsCreatingChat(false) // End loading
                        },
                        onError: (error) => {
                            console.error('Failed to create chat:', error)
                            setIsCreatingChat(false) // End loading
                            setAiStatus(null) // Clear processing status on error
                        },
                    })
                } else {
                    // Add user message to chat (will be saved automatically)
                    addMessage(userMessage)
                    trackMessageSaved(currentChat.chatID, userMessage)
                    sendMessageToSocket(currentChat.chatID, textToSend)
                }
            }
            // Store the query for potential NEEDS_REASONING response
            lastUserQueryRef.current = textToSend
            // Always clear input field after sending
            setInputMessage('')
        }
    }

    const handleNewChat = () => {
        clearChatState()
    }

    const handleDescribeDataset = () => {
        if (!selectedInbox || !isConnected) return

        if (!currentChat) {
            // No current chat - need to create one first (full experience only)
            setIsCreatingChat(true)
            createChat({
                title: 'Dataset Description', // Use descriptive title
                onSuccess: (response) => {
                    if (response?.chatID) {
                        // Mark as recently created to prevent history fetching
                        setRecentlyCreatedChatID(response.chatID)

                        // Set current chat from backend response
                        setCurrentChatFromBackend(response)

                        // Send describe dataset request with real chat ID
                        const request = {
                            action: 'describeDataset',
                            inboxID: selectedInbox.inboxID,
                            chatID: response.chatID,
                        }
                        sendMessage(request)
                        setHasDescribedDataset(true)

                        // Update localStorage with the real chat ID
                        saveLastSelectedChat(response.chatID)

                        // Exit centered mode
                        if (isCenteredMode) setIsCenteredMode(false)
                    }
                    setIsCreatingChat(false)
                },
                onError: (error) => {
                    console.error('Failed to create chat for dataset description:', error)
                    setIsCreatingChat(false)
                },
            })
        } else {
            // Current chat exists - send describe dataset request directly
            const request = {
                action: 'describeDataset',
                inboxID: selectedInbox.inboxID,
                chatID: currentChat.chatID,
            }
            sendMessage(request)
            setHasDescribedDataset(true)
        }
    }

    const handleExport = (format) => {
        if (!currentChat || !isConnected) return
        const request = {
            action: format === 'excel' ? 'exportExcel' : 'exportCSV',
            chatId: currentChat.chatID,
        }
        sendMessage(request)
    }

    const handleResetContext = () => {
        if (!currentChat || !selectedInbox) return
        trackEvent(EVENTS.SCOOP_AI.RESET_CONTEXT)

        reset({
            chatID: currentChat.chatID,
            inboxID: selectedInbox.inboxID,
            onError: (error) => {
                console.error('Failed to reset context:', error)
            },
        })
    }

    const handleRenameChat = (chatID, newTitle) => {
        updateChat({
            chatID,
            title: newTitle,
            onError: (error) => {
                console.error('Failed to rename chat:', error)
                // TODO: Show user-friendly error message
            },
        })
    }

    const handleDeleteChat = (chatID) => {
        const isDeletingCurrentChat = currentChat?.chatID === chatID
        // If deleting the current chat, clear all related states immediately
        if (isDeletingCurrentChat) clearChatState()
        deleteChat({
            chatID,
            userID,
            workspaceID,
            onSuccess: () => {
                // Additional cleanup is already done above if needed
            },
            onError: (error) => {
                console.error('Failed to delete chat:', error)
            },
        })
    }

    const handleSelectChat = (chatID) => {
        // Set switching flag if we're changing to a different chat
        if (currentChat?.chatID !== chatID) {
            setIsSwitchingChats(true)
        }

        // Clear recently created flag when manually selecting a chat (we want to load its history)
        setRecentlyCreatedChatID(null)

        // Smart invalidation: only invalidate the CURRENT chat's history if we saved messages to it
        // This ensures other users/sessions will see our messages when they switch to this chat
        if (currentChat?.chatID && chatsWithSavedMessages.has(currentChat.chatID)) {
            invalidateMessages(currentChat.chatID)
            // Remove from tracking set since we've invalidated
            setChatsWithSavedMessages((prev) => {
                const newSet = new Set(prev)
                newSet.delete(currentChat.chatID)
                return newSet
            })
        }

        selectChat(chatID)
        saveLastSelectedChat(chatID)
    }

    // Helper function to track when messages are saved to backend
    const trackMessageSaved = (chatID, message) => {
        // Only track if this message will be saved to backend (same logic as in useMessages.addMessage)
        const shouldSave =
            !isPublic &&
            !message.needsReasoning && // Exclude UI-only prompts
            (message.role === 'user' || message.role === 'scoop')

        if (shouldSave && chatID) {
            setChatsWithSavedMessages((prev) => new Set([...prev, chatID]))
        }
    }

    const handleSelectInbox = (inboxID) => {
        const newInbox = workspaceMetadata?.inboxes.find((x) => x.inboxID === inboxID)
        setSelectedInbox(newInbox)
    }

    // Wrapper functions to persist settings changes
    const handleAnalysisModeChange = (mode) => {
        if (mode === 'quick') {
            trackEvent(EVENTS.SCOOP_AI.SWITCH_QUICK_MODE)
        } else {
            trackEvent(EVENTS.SCOOP_AI.SWITCH_EXTENDED_THINKING)
        }
        setAnalysisMode(mode)
        if (!isPublic && userID && workspaceID) {
            localStorage.setItem(`${storageKeyPrefix}_analysisMode`, mode)
        }
    }

    const handleShowProcessingStepsChange = (show) => {
        setShowProcessingSteps(show)
        if (!isPublic && userID && workspaceID) {
            localStorage.setItem(`${storageKeyPrefix}_showProcessingSteps`, show.toString())
        }
    }

    const handleShowQueryDetailsChange = (show) => {
        setShowQueryDetails(show)
        if (!isPublic && userID && workspaceID) {
            localStorage.setItem(`${storageKeyPrefix}_showQueryDetails`, show.toString())
        }
    }

    const handleStarterPrompt = (promptText) => {
        // Check prompt limit for demo mode
        if (!canSendPrompt()) {
            const limitMessage = getLimitMessage()
            if (limitMessage) {
                let chatToUse = currentChat
                if (!chatToUse) {
                    chatToUse = createNewChat()
                }
                addMessage({
                    messageID: Date.now(),
                    text: limitMessage,
                    role: 'system',
                    timestamp: new Date().toISOString(),
                })
            }
            return
        }

        // Check if connection is ready
        if (!isConnected) {
            return
        }

        // Use the unified handleSendMessage function
        handleSendMessage(promptText)
    }

    const renderMessage = (message, index) => {
        // Determine if this is the last message with export actions
        // Check if this message has visualization or chart data that would trigger export buttons
        const hasVisualization =
            message.content?.visualization ||
            message.content?.text?.visualization ||
            (message.content?.text?.view && message.content?.text?.selectedItems)

        // Find the last message index with visualization
        let lastVisualizationIndex = -1
        for (let i = convertedMessages.length - 1; i >= 0; i--) {
            const msg = convertedMessages[i]
            const hasViz =
                msg.content?.visualization ||
                msg.content?.text?.visualization ||
                (msg.content?.text?.view && msg.content?.text?.selectedItems)
            if (hasViz) {
                lastVisualizationIndex = i
                break
            }
        }

        const isLastVisualization = hasVisualization && index === lastVisualizationIndex

        // Check if this is the last message in the chat
        const isLastMessage = index === convertedMessages.length - 1

        // Check if this message is from current session (not historical)
        // Real-time messages have numeric messageIDs, backend messages have "msg_..." messageIDs
        const isCurrentSession = message.messageID && !String(message.messageID).startsWith('msg_')

        return (
            <ChatMessage
                key={message.messageID || index}
                message={message}
                selectedInbox={selectedInbox}
                currentChat={currentChat}
                analysisMode={analysisMode}
                sendMessage={sendMessage}
                addMessage={addMessage}
                setAiStatus={setAiStatus}
                inputMessage={inputMessage}
                setLastUserQuery={(query) => {
                    lastUserQueryRef.current = query
                }}
                showProcessingSteps={showProcessingSteps}
                showQueryDetails={showQueryDetails}
                lastFollowUpPrompts={lastFollowUpPromptsRef.current}
                isPublic={isPublic}
                isProcessing={!!aiStatus}
                canSendPrompt={canSendPrompt}
                incrementPromptCount={incrementPromptCount}
                getLimitMessage={getLimitMessage}
                handleSendMessage={handleSendMessage}
                isEmbedded={isEmbedded}
                handleExport={handleExport}
                isLastVisualization={isLastVisualization}
                setSelectedTab={setSelectedTab}
                mlConfig={mlConfig}
                setMlConfig={setMlConfig}
                setMLResults={setMLResults}
                submitFeedback={submitFeedback}
                isSubmittingFeedback={isSubmittingFeedback}
                isLastMessage={isLastMessage}
                isCurrentSession={isCurrentSession}
                isTourWorkspace={isTourWorkspace}
            />
        )
    }

    // Check if actions should be blocked
    const blockActions = !!aiStatus || !isConnected || !selectedInbox
    // Check if input should be disabled due to prompt limit or chat creation
    const inputDisabled = blockActions || (isPublic && promptsRemaining === 0) || isCreatingChat

    // Get container class based on mode
    const getContainerClass = () => {
        if (isEmbedded) return `${styles.container} ${styles.embedded}`
        if (isPublic) return `${styles.container} ${styles.public}`
        return `${styles.container} ${styles.authenticated}`
    }

    return (
        <div className={getContainerClass()} style={embedConfig.customStyles}>
            <div
                className={`${styles.content} ${drawerOpen ? styles.drawerOpen : styles.drawerClosed}`}
            >
                {/* Demo Mode Indicator */}
                <DemoModeIndicator isPublic={isPublic} promptsRemaining={promptsRemaining} />

                {/* Drawer toggle button - hide in demo mode */}
                {!isPublic && (
                    <ScoopButton
                        variant="text"
                        size="xxs"
                        onClick={() => setDrawerOpen(!drawerOpen)}
                        className={`${styles.drawerToggle} ${drawerOpen ? styles.open : styles.closed}`}
                    >
                        <ArrowSquareRightIcon
                            size={18}
                            className={`${styles.drawerToggleIcon} ${drawerOpen ? styles.open : styles.closed}`}
                        />
                    </ScoopButton>
                )}

                <Messages
                    // Message data
                    convertedMessages={convertedMessages}
                    hasMoreMessages={hasMoreMessages}
                    loadMoreMessages={loadMoreMessages}
                    isFetchingMessages={isFetchingMessages}
                    // Loading states
                    loadingChats={loadingChats}
                    loadingChatMessages={loadingChatMessages}
                    chatMessagesError={chatMessagesError}
                    isSwitchingChats={isSwitchingChats}
                    // Chat state
                    currentChat={currentChat}
                    aiStatus={aiStatus}
                    processingSteps={processingSteps}
                    // UI state
                    isCenteredMode={isCenteredMode}
                    isPublic={isPublic}
                    isEmbedded={isEmbedded}
                    selectedInbox={selectedInbox}
                    showProcessingSteps={showProcessingSteps}
                    // ML/Data Science Studio props
                    setSelectedTab={setSelectedTab}
                    mlConfig={mlConfig}
                    setMlConfig={setMlConfig}
                    setMLResults={setMLResults}
                    // Scroll state
                    scrollPositionBeforePagination={scrollPositionBeforePagination}
                    setScrollPositionBeforePagination={setScrollPositionBeforePagination}
                    lastMessageCount={lastMessageCount}
                    setLastMessageCount={setLastMessageCount}
                    // Feedback mutation
                    submitFeedback={submitFeedback}
                    isSubmittingFeedback={isSubmittingFeedback}
                    // Render function
                    renderMessage={renderMessage}
                />

                {/* Chat Input Box Component - Hide during initial loading */}
                {!(loadingChats && !isPublic) && (
                    <ChatInputBox
                        isCenteredMode={isCenteredMode}
                        drawerOpen={drawerOpen}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        handleSendMessage={handleSendMessage}
                        isConnected={isConnected}
                        inputDisabled={inputDisabled}
                        selectedInbox={selectedInbox}
                        workspaceMetadata={workspaceMetadata}
                        handleSelectInbox={handleSelectInbox}
                        analysisMode={analysisMode}
                        setAnalysisMode={handleAnalysisModeChange}
                        showProcessingSteps={showProcessingSteps}
                        setShowProcessingSteps={handleShowProcessingStepsChange}
                        showQueryDetails={showQueryDetails}
                        setShowQueryDetails={handleShowQueryDetailsChange}
                        isPublic={isPublic}
                        promptsRemaining={promptsRemaining}
                        handleNewChat={handleNewChat}
                        currentChat={currentChat}
                        aiStatus={aiStatus}
                        error={error}
                        isCreatingChat={isCreatingChat}
                        handleDescribeDataset={handleDescribeDataset}
                        handleResetContext={handleResetContext}
                    />
                )}
            </div>

            {/* Left Drawer Component */}
            <LeftDrawer
                drawerOpen={drawerOpen}
                persistentChats={persistentChats}
                loadingChats={loadingChats}
                currentChat={currentChat}
                selectChat={handleSelectChat}
                handleRenameChat={handleRenameChat}
                handleDeleteChat={handleDeleteChat}
                handleNewChat={handleNewChat}
                handleStarterPrompt={handleStarterPrompt}
                isChatDeleting={isChatDeleting}
            />
        </div>
    )
}
